import { z } from "zod";

export const idSchema = z.uuid();

/** ISO-8601 timestamp as serialized by `Date.prototype.toISOString()`. */
export const isoDateTimeSchema = z.iso.datetime();

export const emailSchema = z
  .string()
  .trim()
  .toLowerCase()
  .pipe(z.email({ error: "Enter a valid email address" }).max(254, "Email is too long"));

export const passwordSchema = z
  .string()
  .min(8, "Password must be at least 8 characters")
  .max(128, "Password must be at most 128 characters")
  .refine((value) => value.trim().length > 0, "Password cannot be blank");

export const personNameSchema = z
  .string()
  .trim()
  .min(1, "Name is required")
  .max(100, "Name must be at most 100 characters");

export const slugSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(2, "Slug must be at least 2 characters")
  .max(48, "Slug must be at most 48 characters")
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "Use lowercase letters, numbers, and single hyphens");

export const httpUrlSchema = z
  .url({ protocol: /^https?$/, error: "Enter a valid http(s) URL" })
  .max(2048);

/** Opaque single-use token delivered by email (verification, reset, invitation). */
export const tokenSchema = z.string().min(20).max(200);

function isValidTimeZone(value: string): boolean {
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: value });
    return true;
  } catch {
    return false;
  }
}

export const timezoneSchema = z
  .string()
  .trim()
  .min(1)
  .max(64)
  .refine(isValidTimeZone, "Unknown time zone");

export const userSummarySchema = z.object({
  id: idSchema,
  name: z.string(),
  email: z.string(),
  avatarUrl: z.string().nullable(),
});
export type UserSummary = z.infer<typeof userSummarySchema>;
